import type { SvgI } from "tweenly"
import React from "react"
import { renderStyles } from "tweenly"
import { dragNodeRefKey } from "../../../../hooks/editor/useHoldDrag"
import useDynamicRefs from "../../../../hooks/useDynamicRefs"

interface Props {
    object: SvgI
    isHiding: boolean
    isLocking: boolean
    isRootTicker: boolean
    canvasScaleCoef: number
    borderCoef: number
    handleSelectObject: (
        event: React.MouseEvent,
        objectId: string,
        animationIds: string[],
        multiSelectKey: boolean
    ) => void
}

const SvgContainer = ({ object, isHiding, isLocking, isRootTicker, handleSelectObject }: Props) => {
    // publishes the element so masterTimeline.slice can build GSAP tweens for it — without
    // this the object is simply missing from the ref map and every animation on it is
    // replaced by a no-op ("Target for objectId ... not found")
    const [, setRef] = useDynamicRefs<HTMLDivElement>()
    const registerDragNode = setRef(dragNodeRefKey(object.id))
    const objectStyles: React.CSSProperties = renderStyles(object, "inline")

    const isHidden = isHiding || object.isHidden
    const isLocked = isLocking || object.isLocked

    const handleClick = (e: React.MouseEvent) => {
        if (isLocked || isRootTicker) return
        handleSelectObject(e, object.id, Object.keys(object.animations), e.shiftKey)
    }

    // Remove fill/background styles from wrapper (they're in SVG content)
    const { backgroundColor, backgroundImage, background, ...wrapperStyles } = objectStyles

    const content = (
        <div
            id={object.id}
            ref={setRef(object.id)}
            onClick={handleClick}
            style={{
                ...wrapperStyles,
                visibility: isHidden ? "hidden" : "visible",
                pointerEvents: isLocked || isRootTicker ? "none" : "auto",
            }}
        >
            {/* svgContent is generated by internal Pencil import converter (converterNodes.helpers.ts),
                not from user input. Contains only sanitized SVG elements (line, polygon, path)
                with numeric coordinates and validated color values. Safe to render directly. */}
            <svg
                viewBox={object.viewBox || "0 0 100 100"}
                width="100%"
                height="100%"
                xmlns="http://www.w3.org/2000/svg"
                preserveAspectRatio="none"
                dangerouslySetInnerHTML={{ __html: object.svgContent }}
            />
        </div>
    )

    /**
     * A flex child is placed by its parent's layout and can never be dragged, so it gets no
     * drag node — the wrapper would become the flex item and this would lose its place in
     * the row. Otherwise `position: relative` makes the wrapper the containing block and
     * gives a multiselection a node to move this object by (see `dragNodeRefKey`); its
     * `transform` belongs to the gesture, never to React.
     */
    if (wrapperStyles.position === "static") return content

    return (
        <div ref={registerDragNode} style={{ position: "relative" }}>
            {content}
        </div>
    )
}

export default SvgContainer
